import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPIC_LABELS = {
  product: "Product",
  comparison: "Comparison",
  buying_guide: "Buying guide",
  authenticity: "Authenticity",
  regulatory: "Regulatory",
  news: "Market news",
  clinical: "Clinical",
  price_guide: "Price guide",
};

export const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/journal/posts?limit=6`)
      .then((r) => setPosts(r.data.posts || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  // Hide section entirely until at least 1 post exists
  if (!loading && posts.length === 0) return null;

  return (
    <section className="py-24 md:py-32 bg-white border-t border-neutral-200" data-testid="blog-section">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex items-end justify-between gap-6 mb-12 flex-wrap">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-4 font-medium">
              Journal
            </div>
            <h2 className="font-heading leading-[0.95] tracking-tight">
              <span className="block text-4xl md:text-6xl font-black">Field notes</span>
              <span className="block text-4xl md:text-6xl font-thin">from the industry.</span>
            </h2>
          </div>
          <Link
            to="/journal"
            className="group inline-flex items-center gap-2 text-sm font-medium border-b border-black pb-1 hover:gap-3 transition-all"
            data-testid="blog-view-more"
          >
            View all posts <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-px gap-y-12 border-l border-t border-neutral-200">
          {posts.map((post) => (
            <Link
              key={post.id}
              to={`/journal/${post.slug}`}
              className="border-r border-b border-neutral-200 bg-white p-6 flex flex-col gap-5 group"
              data-testid={`blog-post-${post.id}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
                <img
                  src={post.image}
                  alt={post.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="flex items-center gap-3 text-[11px] tracking-[0.2em] uppercase text-neutral-500 font-medium">
                <span>{TOPIC_LABELS[post.topic_type] || post.topic_type}</span>
                <span className="w-1 h-1 bg-neutral-400 rounded-full" />
                <span>{post.reading_time_min} min read</span>
              </div>
              <h3 className="font-heading text-xl md:text-2xl font-black tracking-tight leading-tight group-hover:underline underline-offset-4 line-clamp-3">
                {post.title}
              </h3>
              <p className="text-sm text-neutral-600 font-light line-clamp-3">{post.subtitle}</p>
              <div className="mt-auto inline-flex items-center gap-2 text-xs font-medium text-black">
                Read article <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
